<template>
  <div>
    <div class="d-flex justify-content-center verification-code">
      <CInput :ref="`code1`"
              v-model="code1"
              :isValid="isValid"
              @keyup="onCodeUpdate($event, 1)"
              @paste="onPaste($event)"
              maxlength="1" 
              class="mx-1"/>
      <CInput :ref="`code2`"
              v-model="code2"
              :isValid="isValid"
              @keyup="onCodeUpdate($event, 2)"
              @paste="onPaste($event)"
              maxlength="1" 
              class="mx-1"/>
      <CInput :ref="`code3`"
              v-model="code3"
              :isValid="isValid"
              @keyup="onCodeUpdate($event, 3)"
              @paste="onPaste($event)"
              maxlength="1" 
              class="mx-1"/>
      <CInput :ref="`code4`"
              v-model="code4"
              :isValid="isValid"
              @keyup="onCodeUpdate($event, 4)"
              @paste="onPaste($event)"
              maxlength="1" 
              class="mx-1"/>
    </div>
    <div class="text-center mb-3" style="min-height: 21px">
      <CSpinner v-if="validating" size="sm" color="primary" class="d-block mx-auto mb-1"/>
      <span v-show="errorMessage && !validating" class="text-danger font-weight-semi-bold">{{ errorMessage }}</span>
    </div>
    
    <!-- Resend section -->
    <div v-if="showResend" class="text-center">
      <PAlert icon="cipInfo"
              content-align="align-items-center"
              :disabled="resendTimeout > 0"
              color="primary"
              :text="resendText || $pgettext('token.resend', 'Haven\'t received the code?')">
        <template #cta>
          <CLink @click="handleResend" style="font-size: 12px" class="mr-1">
            <span v-if="resendTimeout > 0">
              <translate translate-context="token.resend">Retry in</translate> {{ resendTimeout }}s
            </span>
            <span v-else v-translate translate-context="token.resend">Resend code</span>
          </CLink>
        </template>
      </PAlert>
    </div>
  </div>
</template>

<script>
import PAlert from "@/components/PAlert.vue";

export default {
  name: 'TokenInput',
  components: { PAlert },
  props: {
    validating: {
      type: Boolean,
      default: false
    },
    errorMessage: {
      type: String,
      default: null
    },
    autoFocus: {
      type: Boolean,
      default: true
    },
    showResend: {
      type: Boolean,
      default: false
    },
    resendText: {
      type: String,
      default: null
    },
    resendTimeoutSeconds: {
      type: Number,
      default: 30
    }
  },
  emits: ['complete', 'update', 'resend'],
  data() {
    return {
      code1: '',
      code2: '',
      code3: '',
      code4: '',
      isPasting: false,
      resendTimeout: 0
    }
  },
  computed: {
    completeCode() {
      return `${this.code1}${this.code2}${this.code3}${this.code4}`
    },
    isValid() {
      if (!this.errorMessage) return undefined
      return false
    }
  },
  mounted() {
    if (this.autoFocus) {
      this.$refs.code1.$el.children[0].focus()
    }
    
    // Add paste event listeners
    this.$refs.code1.$el.children[0].addEventListener('paste', this.onPaste)
    this.$refs.code2.$el.children[0].addEventListener('paste', this.onPaste)
    this.$refs.code3.$el.children[0].addEventListener('paste', this.onPaste)
    this.$refs.code4.$el.children[0].addEventListener('paste', this.onPaste)
    
    // Start resend timeout if showResend is enabled
    if (this.showResend) {
      this.startResendTimeout()
    }
  },
  beforeDestroy() {
    // Clean up event listeners
    this.$refs.code1.$el.children[0].removeEventListener('paste', this.onPaste)
    this.$refs.code2.$el.children[0].removeEventListener('paste', this.onPaste)
    this.$refs.code3.$el.children[0].removeEventListener('paste', this.onPaste)
    this.$refs.code4.$el.children[0].removeEventListener('paste', this.onPaste)
  },
  methods: {
    async onPaste(event) {
      this.isPasting = true
      const paste = (event.clipboardData || window.clipboardData).getData('text')
      
      this.code1 = paste[0] || ''
      this.code2 = paste[1] || ''
      this.code3 = paste[2] || ''
      this.code4 = paste[3] || ''
      
      await this.$nextTick()
      await new Promise(resolve => setTimeout(resolve, 100))
      
      this.$refs.code4.$el.children[0].focus()
      await this.onCodeUpdate(event, 4)
      this.isPasting = false
      
      event.preventDefault()
    },
    async onCodeUpdate(event, index) {
      if (this.handleKeys(event, index)) return
      
      this.$emit('update', this.completeCode)
      
      if (this.completeCode.length < 4) {
        this.focusNextInput(index)
        return
      }
      
      if (!this.isPasting) {
        this.$emit('complete', this.completeCode)
      }
    },
    handleKeys(event, index) {
      if (event.key === 'Backspace') {
        if (index > 1) {
          this.focusPreviousInput(index)
        }
        return true
      }
      if (event.key === 'ArrowLeft') {
        this.focusPreviousInput(index)
        return true
      }
      if (event.key === 'ArrowRight') {
        this.focusNextInput(index)
        return true
      }
      return false
    },
    async focusPreviousInput(index) {
      if (index <= 1) return
      await this.$nextTick()
      const prevInput = this.$refs[`code${index - 1}`].$el.children[0]
      prevInput.focus()
      prevInput.select()
    },
    focusNextInput(index) {
      if (index < 4) {
        this.$refs[`code${index + 1}`].$el.children[0].focus()
      }
    },
    reset() {
      this.code1 = ''
      this.code2 = ''
      this.code3 = ''
      this.code4 = ''
      if (this.autoFocus) {
        this.$refs.code1.$el.children[0].focus()
      }
    },
    focus() {
      this.$refs.code1.$el.children[0].focus()
    },
    startResendTimeout() {
      this.resendTimeout = this.resendTimeoutSeconds
      this.updateResendTimeout()
    },
    updateResendTimeout() {
      if (this.resendTimeout > 0) {
        setTimeout(() => {
          this.resendTimeout--
          this.updateResendTimeout()
        }, 1000)
      }
    },
    handleResend() {
      if (this.resendTimeout > 0) return
      
      this.$emit('resend')
      this.startResendTimeout()
      this.reset()
    }
  }
}
</script>

<style scoped>
.verification-code input {
  width: 50px;
  height: 50px;
  text-align: center;
  font-size: 24px;
  font-weight: 600;
}
</style>